// Random generator utilities for fair and transparent player number assignment
import { Player, PlayerNumberAssignment, RandomGeneratorStep, RandomGenerationResult } from '../types';

// Seeded random number generator for reproducible results
class SeededRandom {
  private seed: number;

  constructor(seed: string) {
    this.seed = this.hashCode(seed);
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  public next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }
}

export function generateRandomPlayerAssignments(
  players: Player[],
  courtCount: number = 3
): RandomGenerationResult {
  const expectedPlayerCount = courtCount * 4;
  if (players.length !== expectedPlayerCount) {
    throw new Error(`Exactly ${expectedPlayerCount} players are required for ${courtCount} courts`);
  }

  // Create a timestamp-based seed for transparency
  const timestamp = new Date().toISOString();
  const seed = `padel_${timestamp}_${players.map(p => p.id).join('_')}`;
  const rng = new SeededRandom(seed);
  
  const steps: RandomGeneratorStep[] = [];
  const assignments: PlayerNumberAssignment[] = [];
  const availableNumbers = Array.from({ length: expectedPlayerCount }, (_, i) => i + 1);
  const shuffledPlayers = [...players];

  steps.push({
    step: 1,
    description: `Starting random assignment with seed: ${seed.substring(0, 50)}...`
  });

  // Fisher-Yates shuffle algorithm with step tracking
  for (let i = shuffledPlayers.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    
    // Swap elements
    [shuffledPlayers[i], shuffledPlayers[j]] = [shuffledPlayers[j], shuffledPlayers[i]];
    
    steps.push({
      step: steps.length + 1,
      description: `Swapping ${shuffledPlayers[i].name} with ${shuffledPlayers[j].name}`
    });
  }

  // Assign numbers sequentially to shuffled players
  shuffledPlayers.forEach((player, index) => {
    const assignedNumber = index + 1;
    assignments.push({
      playerId: player.id,
      playerName: player.name,
      assignedNumber
    });

    steps.push({
      step: steps.length + 1,
      description: `Assigned number ${assignedNumber} to ${player.name}`,
      playerName: player.name,
      assignedNumber
    });
  });

  steps.push({
    step: steps.length + 1,
    description: 'Random assignment completed successfully!'
  });

  return {
    assignments,
    steps,
    seed
  };
}

// Generate the exact match pattern based on court count
export function generateMatchPattern(
  assignments: PlayerNumberAssignment[],
  courtCount: number = 3,
  isTrial: boolean = false
): {
  rounds: Array<{
    roundNumber: number;
    courts: Array<{
      courtNumber: number;
      pair1: { player1: string; player2: string };
      pair2: { player1: string; player2: string };
    }>;
  }>;
} {
  // Create a mapping from assigned numbers to player names
  const numberToPlayerName = assignments.reduce((map, assignment) => {
    map[assignment.assignedNumber] = assignment.playerName;
    return map;
  }, {} as Record<number, string>);

  // Get the appropriate pattern based on court count
  let pattern: any[] = [];
  
  // For trial days, use 6-round patterns optimized for maximum player variety
  if (isTrial && courtCount === 2) {
    // 2 courts, 8 players, 6 rounds - Each player partners with 6 different players
    pattern = [
      {
        roundNumber: 1,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } },
          { courtNumber: 2, pair1: { player1: 5, player2: 6 }, pair2: { player1: 7, player2: 8 } }
        ]
      },
      {
        roundNumber: 2,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 3 }, pair2: { player1: 5, player2: 7 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 4 }, pair2: { player1: 6, player2: 8 } }
        ]
      },
      {
        roundNumber: 3,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 5 }, pair2: { player1: 2, player2: 6 } },
          { courtNumber: 2, pair1: { player1: 3, player2: 7 }, pair2: { player1: 4, player2: 8 } }
        ]
      },
      {
        roundNumber: 4,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 4 }, pair2: { player1: 6, player2: 7 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 3 }, pair2: { player1: 5, player2: 8 } }
        ]
      },
      {
        roundNumber: 5,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 6 }, pair2: { player1: 4, player2: 5 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 7 }, pair2: { player1: 3, player2: 8 } }
        ]
      },
      {
        roundNumber: 6,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 7 }, pair2: { player1: 3, player2: 6 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 5 }, pair2: { player1: 4, player2: 8 } }
        ]
      }
    ];
  } else if (isTrial && courtCount === 3) {
    // 3 courts, 12 players, 6 rounds - Optimized for maximum partner and opponent variety
    pattern = [
      {
        roundNumber: 1,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } },
          { courtNumber: 2, pair1: { player1: 5, player2: 6 }, pair2: { player1: 7, player2: 8 } },
          { courtNumber: 3, pair1: { player1: 9, player2: 10 }, pair2: { player1: 11, player2: 12 } }
        ]
      },
      {
        roundNumber: 2,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 3 }, pair2: { player1: 5, player2: 9 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 7 }, pair2: { player1: 6, player2: 11 } },
          { courtNumber: 3, pair1: { player1: 4, player2: 10 }, pair2: { player1: 8, player2: 12 } }
        ]
      },
      {
        roundNumber: 3,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 5 }, pair2: { player1: 2, player2: 8 } },
          { courtNumber: 2, pair1: { player1: 3, player2: 9 }, pair2: { player1: 4, player2: 11 } },
          { courtNumber: 3, pair1: { player1: 6, player2: 12 }, pair2: { player1: 7, player2: 10 } }
        ]
      },
      {
        roundNumber: 4,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 7 }, pair2: { player1: 4, player2: 6 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 11 }, pair2: { player1: 5, player2: 10 } },
          { courtNumber: 3, pair1: { player1: 3, player2: 12 }, pair2: { player1: 8, player2: 9 } }
        ]
      },
      {
        roundNumber: 5,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 9 }, pair2: { player1: 6, player2: 10 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 4 }, pair2: { player1: 7, player2: 12 } },
          { courtNumber: 3, pair1: { player1: 3, player2: 5 }, pair2: { player1: 8, player2: 11 } }
        ]
      },
      {
        roundNumber: 6,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 11 }, pair2: { player1: 3, player2: 8 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 9 }, pair2: { player1: 4, player2: 12 } },
          { courtNumber: 3, pair1: { player1: 5, player2: 7 }, pair2: { player1: 6, player2: 10 } }
        ]
      }
    ];
  } else if (isTrial && courtCount === 4) {
    // 4 courts, 16 players, 6 rounds - Optimized for maximum partner and opponent variety
    pattern = [
      {
        roundNumber: 1,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } },
          { courtNumber: 2, pair1: { player1: 5, player2: 6 }, pair2: { player1: 7, player2: 8 } },
          { courtNumber: 3, pair1: { player1: 9, player2: 10 }, pair2: { player1: 11, player2: 12 } },
          { courtNumber: 4, pair1: { player1: 13, player2: 14 }, pair2: { player1: 15, player2: 16 } }
        ]
      },
      {
        roundNumber: 2,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 3 }, pair2: { player1: 5, player2: 9 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 8 }, pair2: { player1: 6, player2: 13 } },
          { courtNumber: 3, pair1: { player1: 4, player2: 11 }, pair2: { player1: 7, player2: 14 } },
          { courtNumber: 4, pair1: { player1: 10, player2: 15 }, pair2: { player1: 12, player2: 16 } }
        ]
      },
      {
        roundNumber: 3,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 5 }, pair2: { player1: 2, player2: 10 } },
          { courtNumber: 2, pair1: { player1: 3, player2: 13 }, pair2: { player1: 4, player2: 15 } },
          { courtNumber: 3, pair1: { player1: 6, player2: 11 }, pair2: { player1: 8, player2: 16 } },
          { courtNumber: 4, pair1: { player1: 7, player2: 12 }, pair2: { player1: 9, player2: 14 } }
        ]
      },
      {
        roundNumber: 4,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 7 }, pair2: { player1: 4, player2: 12 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 14 }, pair2: { player1: 5, player2: 11 } },
          { courtNumber: 3, pair1: { player1: 3, player2: 10 }, pair2: { player1: 6, player2: 15 } },
          { courtNumber: 4, pair1: { player1: 8, player2: 9 }, pair2: { player1: 13, player2: 16 } }
        ]
      },
      {
        roundNumber: 5,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 9 }, pair2: { player1: 6, player2: 12 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 4 }, pair2: { player1: 7, player2: 16 } },
          { courtNumber: 3, pair1: { player1: 3, player2: 8 }, pair2: { player1: 10, player2: 14 } },
          { courtNumber: 4, pair1: { player1: 5, player2: 15 }, pair2: { player1: 11, player2: 13 } }
        ]
      },
      {
        roundNumber: 6,
        courts: [
          { courtNumber: 1, pair1: { player1: 1, player2: 11 }, pair2: { player1: 3, player2: 14 } },
          { courtNumber: 2, pair1: { player1: 2, player2: 6 }, pair2: { player1: 4, player2: 9 } },
          { courtNumber: 3, pair1: { player1: 5, player2: 8 }, pair2: { player1: 7, player2: 10 } },
          { courtNumber: 4, pair1: { player1: 12, player2: 15 }, pair2: { player1: 13, player2: 16 } }
        ]
      }
    ];
  }
  
  // If not trial or trial patterns handled above, use regular 4-round patterns
  if (pattern.length === 0) {
    switch (courtCount) {
      case 1: // 4 players, 1 court, 3 rounds (all best-of-9)
        pattern = [
          {
            roundNumber: 1,
            courts: [
              { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } }
          ]
        },
        {
          roundNumber: 2,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 3 }, pair2: { player1: 2, player2: 4 } }
          ]
        },
        {
          roundNumber: 3,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 4 }, pair2: { player1: 2, player2: 3 } }
          ]
        }
      ];
      break;
      
    case 2: // 8 players pattern - Must match server pattern exactly
      pattern = [
        {
          roundNumber: 1,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } },
            { courtNumber: 2, pair1: { player1: 5, player2: 6 }, pair2: { player1: 7, player2: 8 } }
          ]
        },
        {
          roundNumber: 2,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 5 }, pair2: { player1: 2, player2: 6 } },
            { courtNumber: 2, pair1: { player1: 3, player2: 7 }, pair2: { player1: 4, player2: 8 } }
          ]
        },
        {
          roundNumber: 3,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 3 }, pair2: { player1: 5, player2: 7 } },
            { courtNumber: 2, pair1: { player1: 2, player2: 4 }, pair2: { player1: 6, player2: 8 } }
          ]
        },
        {
          roundNumber: 4,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 7 }, pair2: { player1: 4, player2: 6 } },
            { courtNumber: 2, pair1: { player1: 2, player2: 8 }, pair2: { player1: 3, player2: 5 } }
          ]
        }
      ];
      break;
      
    case 3: // 12 players pattern (existing)
      pattern = [
        {
          roundNumber: 1,
          courts: [
            { courtNumber: 1, pair1: { player1: 10, player2: 11 }, pair2: { player1: 2, player2: 9 } },
            { courtNumber: 2, pair1: { player1: 4, player2: 5 }, pair2: { player1: 8, player2: 1 } },
            { courtNumber: 3, pair1: { player1: 6, player2: 12 }, pair2: { player1: 3, player2: 7 } }
          ]
        },
        {
          roundNumber: 2,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 7 }, pair2: { player1: 9, player2: 8 } },
            { courtNumber: 2, pair1: { player1: 12, player2: 11 }, pair2: { player1: 5, player2: 2 } },
            { courtNumber: 3, pair1: { player1: 6, player2: 3 }, pair2: { player1: 10, player2: 4 } }
          ]
        },
        {
          roundNumber: 3,
          courts: [
            { courtNumber: 1, pair1: { player1: 7, player2: 5 }, pair2: { player1: 3, player2: 2 } },
            { courtNumber: 2, pair1: { player1: 8, player2: 10 }, pair2: { player1: 12, player2: 9 } },
            { courtNumber: 3, pair1: { player1: 11, player2: 6 }, pair2: { player1: 1, player2: 4 } }
          ]
        },
        {
          roundNumber: 4,
          courts: [
            { courtNumber: 1, pair1: { player1: 2, player2: 7 }, pair2: { player1: 9, player2: 4 } },
            { courtNumber: 2, pair1: { player1: 6, player2: 10 }, pair2: { player1: 5, player2: 8 } },
            { courtNumber: 3, pair1: { player1: 1, player2: 11 }, pair2: { player1: 12, player2: 3 } }
          ]
        }
      ];
      break;
      
    case 4: // 16 players pattern - Must match server pattern exactly
      pattern = [
        {
          roundNumber: 1,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } },
            { courtNumber: 2, pair1: { player1: 5, player2: 6 }, pair2: { player1: 7, player2: 8 } },
            { courtNumber: 3, pair1: { player1: 9, player2: 10 }, pair2: { player1: 11, player2: 12 } },
            { courtNumber: 4, pair1: { player1: 13, player2: 14 }, pair2: { player1: 15, player2: 16 } }
          ]
        },
        {
          roundNumber: 2,
          courts: [
            { courtNumber: 1, pair1: { player1: 10, player2: 14 }, pair2: { player1: 8, player2: 4 } },
            { courtNumber: 2, pair1: { player1: 16, player2: 7 }, pair2: { player1: 1, player2: 12 } },
            { courtNumber: 3, pair1: { player1: 9, player2: 6 }, pair2: { player1: 15, player2: 2 } },
            { courtNumber: 4, pair1: { player1: 11, player2: 5 }, pair2: { player1: 3, player2: 13 } }
          ]
        },
        {
          roundNumber: 3,
          courts: [
            { courtNumber: 1, pair1: { player1: 3, player2: 14 }, pair2: { player1: 9, player2: 7 } },
            { courtNumber: 2, pair1: { player1: 12, player2: 13 }, pair2: { player1: 6, player2: 4 } },
            { courtNumber: 3, pair1: { player1: 10, player2: 5 }, pair2: { player1: 15, player2: 1 } },
            { courtNumber: 4, pair1: { player1: 2, player2: 16 }, pair2: { player1: 11, player2: 8 } }
          ]
        },
        {
          roundNumber: 4,
          courts: [
            { courtNumber: 1, pair1: { player1: 16, player2: 9 }, pair2: { player1: 4, player2: 5 } },
            { courtNumber: 2, pair1: { player1: 10, player2: 2 }, pair2: { player1: 7, player2: 13 } },
            { courtNumber: 3, pair1: { player1: 6, player2: 14 }, pair2: { player1: 11, player2: 1 } },
            { courtNumber: 4, pair1: { player1: 3, player2: 8 }, pair2: { player1: 15, player2: 12 } }
          ]
        }
      ];
      break;
      
    case 5: // 20 players pattern - Must match server pattern exactly
      pattern = [
        {
          roundNumber: 1,
          courts: [
            { courtNumber: 1, pair1: { player1: 1, player2: 2 }, pair2: { player1: 3, player2: 4 } },
            { courtNumber: 2, pair1: { player1: 5, player2: 6 }, pair2: { player1: 7, player2: 8 } },
            { courtNumber: 3, pair1: { player1: 9, player2: 10 }, pair2: { player1: 11, player2: 12 } },
            { courtNumber: 4, pair1: { player1: 13, player2: 14 }, pair2: { player1: 15, player2: 16 } },
            { courtNumber: 5, pair1: { player1: 17, player2: 18 }, pair2: { player1: 19, player2: 20 } }
          ]
        },
        {
          roundNumber: 2,
          courts: [
            { courtNumber: 1, pair1: { player1: 7, player2: 19 }, pair2: { player1: 9, player2: 13 } },
            { courtNumber: 2, pair1: { player1: 16, player2: 2 }, pair2: { player1: 5, player2: 18 } },
            { courtNumber: 3, pair1: { player1: 4, player2: 6 }, pair2: { player1: 17, player2: 12 } },
            { courtNumber: 4, pair1: { player1: 15, player2: 3 }, pair2: { player1: 10, player2: 20 } },
            { courtNumber: 5, pair1: { player1: 8, player2: 11 }, pair2: { player1: 1, player2: 14 } }
          ]
        },
        {
          roundNumber: 3,
          courts: [
            { courtNumber: 1, pair1: { player1: 20, player2: 14 }, pair2: { player1: 7, player2: 2 } },
            { courtNumber: 2, pair1: { player1: 13, player2: 5 }, pair2: { player1: 4, player2: 10 } },
            { courtNumber: 3, pair1: { player1: 1, player2: 16 }, pair2: { player1: 19, player2: 12 } },
            { courtNumber: 4, pair1: { player1: 8, player2: 17 }, pair2: { player1: 9, player2: 3 } },
            { courtNumber: 5, pair1: { player1: 6, player2: 15 }, pair2: { player1: 18, player2: 11 } }
          ]
        },
        {
          roundNumber: 4,
          courts: [
            { courtNumber: 1, pair1: { player1: 16, player2: 3 }, pair2: { player1: 7, player2: 11 } },
            { courtNumber: 2, pair1: { player1: 18, player2: 14 }, pair2: { player1: 9, player2: 4 } },
            { courtNumber: 3, pair1: { player1: 8, player2: 13 }, pair2: { player1: 20, player2: 12 } },
            { courtNumber: 4, pair1: { player1: 1, player2: 5 }, pair2: { player1: 17, player2: 15 } },
            { courtNumber: 5, pair1: { player1: 19, player2: 2 }, pair2: { player1: 10, player2: 6 } }
          ]
        }
      ];
      break;
      
    default:
      throw new Error(`Unsupported court count: ${courtCount}. Supported counts are 1, 2, 3, 4, and 5.`);
    }
  } // End of if (pattern.length === 0) - regular patterns

  // Convert number pattern to player name pattern
  const rounds = pattern.map(round => ({
    roundNumber: round.roundNumber,
    courts: round.courts.map((court: any) => ({
      courtNumber: court.courtNumber,
      pair1: {
        player1: numberToPlayerName[court.pair1.player1],
        player2: numberToPlayerName[court.pair1.player2]
      },
      pair2: {
        player1: numberToPlayerName[court.pair2.player1],
        player2: numberToPlayerName[court.pair2.player2]
      }
    }))
  }));

  return { rounds };
}